import { Constants as ST } from "./session";
import { exportForST, uuidv4 } from "./util";
import { findWindow, isValidWindow, windowInfo } from "./windowManager";

exportForST({
    ServerMessage: {
        RegisterMessageHandler: registerServerMessage
    }
});

const EVENTSOURCE_WINDOW_KEY = 'ST_SESSION_ServerMessage_Window';
const EVENTSOURCE_INSTANCE_KEY = 'ST_SESSION_ServerMessage_Instance';
const instanceId = uuidv4();
const registeredCallbacks: Record<string, Function> = {};
const channel = new BroadcastChannel("ServerEventMessages");
let wsEvents: EventSource | undefined;

/**
 * Register a listener for messages sent from the server.
 * @param name The class name of the message being sent from the server
 * @param callback A callback handler. Only one handler for each message is allowed
 * @returns An unregister method to remove the listener
 */
export function registerServerMessage<T>(name: string, callback: (data: T) => void) {
    registeredCallbacks[name] = callback;
    registerEventListener(name);

    channel.postMessage({
        registeredCallback: name
    });

    return function unregister() {
        delete registeredCallbacks[name];
    };
}

document.addEventListener('DOMContentLoaded', () => {
    if (!ST.disablePolling || (window.ST as any).DisablePolling === false) {
        // poll and take ownership if the current event source when owner is closed
        setInterval(checkSingletonOwnership, 5000);
        checkSingletonOwnership();

        function checkSingletonOwnership() {
            if (localStorage[EVENTSOURCE_WINDOW_KEY] === windowInfo().windowName
                && localStorage[EVENTSOURCE_INSTANCE_KEY] === windowInfo().instance) {
                if (!wsEvents) {
                    initServerEvents();
                }
                return;
            }

            wsEvents?.close();
            wsEvents = undefined;

            if (!isValidWindow(localStorage[EVENTSOURCE_WINDOW_KEY], localStorage[EVENTSOURCE_INSTANCE_KEY])) {
                localStorage[EVENTSOURCE_WINDOW_KEY] = windowInfo().windowName;
                localStorage[EVENTSOURCE_INSTANCE_KEY] = windowInfo().instance;
                initServerEvents();
            }
        }
    }
});

// Listen on the broadcast channel for any events being posted from the singleton wsEvents
channel.onmessage = (msg) => {
    if (msg.data?.name) {
        dispatchMessage(msg.data?.name, msg.data?.data);
    }
    else if (msg.data?.registeredCallback) {
        const name = msg.data?.registeredCallback as string;

        // need to register a dummy method so if this window takes possession of wsEvents
        // we can re-register this callback
        if (!registeredCallbacks[name]) {
            registeredCallbacks[name] = () => { };
            registerEventListener(name);
        }
    }
};

function dispatchMessage(name: string, data: unknown) {
    const callback = registeredCallbacks[name];
    if (callback) {
        try {
            callback(data);
        }
        catch (e) {
            console.error(e);
        }
        
    }
}

function registerEventListener(name: string) {
    wsEvents?.addEventListener(name, (ev) => {
        const data = JSON.parse(ev.data);

        dispatchMessage(name, data);
        channel.postMessage({ name, data });
    })
}

function initServerEvents() {
    const isMobile = document.body.classList.contains('Mobile');

    if (!ST.impersonated && !wsEvents && !isMobile) {
        let errCount = 0;
        let errTimer: NodeJS.Timeout;

        console.log(`%cConnecting To SSE EventSource (${instanceId})`, 'color: green');
        wsEvents = new EventSource('api/v1/st_sse');

        // Add a grace period for errors
        wsEvents.onerror = function () {
            clearTimeout(errTimer);
            errTimer = setTimeout(() => errCount = 0, 5000);

            if (wsEvents && errCount++ > 3) {
                wsEvents.close();
                wsEvents = undefined;
            }
        };

        Object.keys(registeredCallbacks).forEach(registerEventListener);
    }
}

registerServerMessage("SSE_Initialized", (data) => {
    console.log('%cServer Side Events Initialized', 'color: green');
});
